import { create } from 'zustand';

export interface FileProgressEntry {
  percent: number;
  currentSpeed?: number; // bytes/s
  status: 'queued' | 'uploading' | 'done' | 'failed';
  rawFile?: File; // retained on failure so detail page can retry without re-selecting
  autoRetryCount: number;
  manualRetryCount: number;
  uploadStartedAt: string | null;
  uploadFinishedAt: string | null;
  avgSpeedMbps: number | null;
}

interface UploadProgressState {
  progress: Record<string, FileProgressEntry>;
  setProgress: (key: string, entry: Partial<FileProgressEntry> & Pick<FileProgressEntry, 'status'>) => void;
  clearProgress: (key: string) => void;
  getProgress: (key: string) => FileProgressEntry | undefined;
  incrementManualRetry: (key: string) => void;
  incrementAutoRetry: (key: string) => void;
}

const DEFAULT_ENTRY: Omit<FileProgressEntry, 'status'> = {
  percent: 0,
  autoRetryCount: 0,
  manualRetryCount: 0,
  uploadStartedAt: null,
  uploadFinishedAt: null,
  avgSpeedMbps: null,
};

export const useUploadProgressStore = create<UploadProgressState>((set, get) => ({
  progress: {},
  setProgress: (key, entry) => {
    set((state) => ({
      progress: {
        ...state.progress,
        [key]: { ...DEFAULT_ENTRY, ...state.progress[key], ...entry },
      },
    }));
  },
  clearProgress: (key) => {
    set((state) => {
      const updated = { ...state.progress };
      delete updated[key];
      return { progress: updated };
    });
  },
  getProgress: (key) => get().progress[key],
  incrementManualRetry: (key) =>
    set((state) => {
      const existing = state.progress[key] ?? { ...DEFAULT_ENTRY, status: 'uploading' as const };
      return {
        progress: {
          ...state.progress,
          [key]: { ...existing, manualRetryCount: existing.manualRetryCount + 1, uploadStartedAt: new Date().toISOString() },
        },
      };
    }),
  incrementAutoRetry: (key) =>
    set((state) => {
      const existing = state.progress[key] ?? { ...DEFAULT_ENTRY, status: 'uploading' as const };
      return {
        progress: {
          ...state.progress,
          [key]: { ...existing, autoRetryCount: existing.autoRetryCount + 1 },
        },
      };
    }),
}));
